import { FunctionComponent, useState } from 'react';
import clsx from 'clsx';
import { MarketplaceIntegrations } from '@spike/marketplace-model';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { useNonInitialEffect } from '@versiondos/hooks';
import { MarketplaceStatus } from '@spike/marketplace-action';
import { BusinessSettingsTitle } from '../UI';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { IntegrationCard } from './IntegrationCard';
import ChannelIntegrationCard from './ChannelIntegrationCard/ChannelIntegrationCard';
import Quickbooks from './Quickbooks';
import Idogcam from './Idogcam';
import { IDOGCAM_ICON_SRC } from './Idogcam/model/IdogcamModel';
import styles from './Integrations.module.scss';

export interface Props {
    integrations?: MarketplaceIntegrations;
    onSave?: (integrations: MarketplaceIntegrations) => void;
    onContinue?: (integrations: MarketplaceIntegrations) => void;
    onChange?: (integrations: MarketplaceIntegrations) => void;
    className?: string;
    openSidebar?: () => void;
    isMobile?: boolean;
}

export const Integrations: FunctionComponent<Props> = props => {
    const [showQuickbooks, setShowQuickbooks] = useState(false);
    const [showIdogcam, setShowIdogcam] = useState(false);

    /*   const loading = useSelector<RootState, boolean>(state => state.marketplace.loading);
  const status = useSelector<RootState, MarketplaceStatus>(state => state.marketplace.status);
  const [saved, setSaved] = useState(false);

  const [integrations, setIntegrations] = useState({...props.integrations});

  useNonInitialEffect(() => {
    props.onChange && props.onChange(integrations);
    setSaved(_.isEqual(props.integrations, integrations));
  },[integrations]);

  useNonInitialEffect(() => {
    if(status === MarketplaceStatus.SaveSuccess || status === MarketplaceStatus.SaveSettingsMarketplaceSuccess) {
      setSaved(true);
    }
  },[status]); */

    /*   const changeValueHandler = (value: string) => {
    setIntegrations(prev => ({...prev, googleAnalyticsTrackingId: value}));
  };

  const clickButtonHandler = () => {
    props.onSave && props.onSave({ ...integrations });
    props.onContinue && props.onContinue({...integrations});
  }; */

    //const button = (<Button label={props.onSave ? (saved ? "Saved!" : "Save") : "Continue"} onClick={clickButtonHandler} loading={loading} />);

    const [gmailStatus, setGmailStatus] = useState<'idle' | 'connecting' | 'connected'>('idle');
    const [googleAdsStatus, setGoogleAdsStatus] = useState<'idle' | 'connecting' | 'connected'>('idle');
    const [metaAdsStatus, setMetaAdsStatus] = useState<'idle' | 'connecting' | 'connected'>('idle');

    const handleQuickBooksClick = () => {
        setShowQuickbooks(true);
    };

    const handleBackToIntegrations = () => {
        setShowQuickbooks(false);
        setShowIdogcam(false);
    };

    // The iDogCam screen handles both states: connect form or camera list.
    const handleIdogcamClick = () => {
        setShowIdogcam(true);
    };

    const handleGmailConnect = () => {
        if (gmailStatus !== 'idle') return;
        setGmailStatus('connecting');
        window.setTimeout(() => setGmailStatus('connected'), 1800);
    };

    const handleGoogleAdsConnect = () => {
        if (googleAdsStatus !== 'idle') return;
        setGoogleAdsStatus('connecting');
        window.setTimeout(() => setGoogleAdsStatus('connected'), 1800);
    };

    const handleMetaAdsConnect = () => {
        if (metaAdsStatus !== 'idle') return;
        setMetaAdsStatus('connecting');
        window.setTimeout(() => setMetaAdsStatus('connected'), 1800);
    };

    if (showIdogcam) {
        // Same wrapper as the integrations list: it owns the desktop padding and inner scroll.
        return (
            <div className={clsx(styles.container, props.className)}>
                <Idogcam onBack={handleBackToIntegrations} />
            </div>
        );
    }

    if (showQuickbooks) {
        return (
            <Quickbooks
                openSidebar={props.openSidebar}
                isMobile={props.isMobile}
                onBack={handleBackToIntegrations}
                title="QuickBooks Integration"
                iconSrc="/images/settings/integrations/quickbooks.svg"
            />
        );
    }

    return (
        <div className={clsx(styles.container, props.className)}>
            <div className={styles.content}>
                <div className={styles.titleContainer}>
                    <BusinessSettingsTitle className={styles.businessSettingsTittle}>
                        {' '}
                        {props.isMobile && (
                            <FontAwesomeIcon
                                className={styles.iconBusinessTitle}
                                onClick={props.openSidebar}
                                icon={faArrowLeft}
                                size="2x"
                            />
                        )}{' '}
                        Integrations
                    </BusinessSettingsTitle>
                </div>
                <h3 className={styles.sectionTitle}>Communication channels</h3>
                <p className={styles.sectionDescription}>
                    Connect your business accounts to send and receive client messages from your Cuddles Inbox.
                </p>
                <div className={clsx(styles.integrationsContainer, styles.sectionCards)}>
                    <ChannelIntegrationCard
                        icon="/images/settings/integrations/gmail.svg"
                        title="Gmail"
                        description={
                            gmailStatus === 'connected'
                                ? 'Connected as hello@happypaws.com. Emails now flow into your Inbox.'
                                : 'Send and receive emails from your Google Workspace account, right from your Inbox.'
                        }
                        status={gmailStatus === 'connected' ? 'connected' : 'notConnected'}
                        buttonLabel={gmailStatus === 'connected' ? 'Manage' : 'Connect'}
                        buttonLoading={gmailStatus === 'connecting'}
                        onClick={handleGmailConnect}
                    />
                    <ChannelIntegrationCard
                        icon="/images/settings/integrations/sms.svg"
                        title="SMS"
                        description="Two-way text messaging with your clients through your dedicated business number."
                        status="connected"
                        buttonLabel="Manage"
                    />
                    <ChannelIntegrationCard
                        icon="/images/settings/integrations/whatsapp.svg"
                        title="WhatsApp Business"
                        description="Message your clients on the world's most popular chat app."
                        status="comingSoon"
                        buttonLabel="Coming Soon"
                        buttonDisabled
                    />
                    <ChannelIntegrationCard
                        icon="/images/settings/integrations/outlook.svg"
                        title="Outlook"
                        description="Connect your Microsoft 365 mailbox to your Cuddles Inbox."
                        status="comingSoon"
                        buttonLabel="Coming Soon"
                        buttonDisabled
                    />
                </div>

                <h3 className={styles.sectionTitle}>Marketing</h3>
                <p className={styles.sectionDescription}>
                    Connect your ad accounts to import campaign spend automatically and measure cost per client and ROAS
                    in your Marketing insights.
                </p>
                <div className={clsx(styles.integrationsContainer, styles.sectionCards)}>
                    <ChannelIntegrationCard
                        icon="/images/settings/integrations/google-ads.svg"
                        title="Google Ads"
                        description={
                            googleAdsStatus === 'connected'
                                ? 'Connected to account 361-180-7938. Campaign spend imports daily.'
                                : 'Import your campaign spend daily and attribute new clients back to each campaign.'
                        }
                        status={googleAdsStatus === 'connected' ? 'connected' : 'notConnected'}
                        buttonLabel={googleAdsStatus === 'connected' ? 'Manage' : 'Connect'}
                        buttonLoading={googleAdsStatus === 'connecting'}
                        onClick={handleGoogleAdsConnect}
                    />
                    <ChannelIntegrationCard
                        icon="/images/settings/integrations/meta.svg"
                        title="Meta Ads"
                        description={
                            metaAdsStatus === 'connected'
                                ? 'Connected to Happy Paws Business account. Campaign spend imports daily.'
                                : 'Import Facebook & Instagram campaign spend and track which ads bring you clients.'
                        }
                        status={metaAdsStatus === 'connected' ? 'connected' : 'notConnected'}
                        buttonLabel={metaAdsStatus === 'connected' ? 'Manage' : 'Connect'}
                        buttonLoading={metaAdsStatus === 'connecting'}
                        onClick={handleMetaAdsConnect}
                    />
                </div>

                <h3 className={styles.sectionTitle}>Accounting</h3>
                <div className={clsx(styles.integrationsContainer, styles.sectionCards)}>
                    <IntegrationCard
                        icon="/images/settings/integrations/quickbooks.svg"
                        title="QuickBooks"
                        description="Export your financial data to QuickBooks in a compatible CSV format."
                        onViewClick={handleQuickBooksClick}
                    />
                </div>

                <h3 className={styles.sectionTitle}>Cameras</h3>
                <div className={clsx(styles.integrationsContainer, styles.sectionCards)}>
                    <IntegrationCard
                        icon={IDOGCAM_ICON_SRC}
                        iconShape="wide"
                        title="iDogCam"
                        description="Manage all your business cameras from one dashboard. Monitor your space in real time."
                        onViewClick={handleIdogcamClick}
                    />
                </div>
                {/* <div className={styles.nameContainer}>
        <TextField
          label="Google Analitycs Tracking ID"
          value={!isEmpty(integrations.googleAnalyticsTrackingId) ? integrations.googleAnalyticsTrackingId! : ""}
          onChange={changeValueHandler}
        />
      </div>
      <div className={clsx(styles.buttonContainer,{[styles.right]:props.onContinue})}>
        {button}
      </div> */}
            </div>
        </div>
    );
};

export default Integrations;
